import React, { useEffect, useState } from 'react';
import { getCompanyLogoSrc } from '../utils/mediaUrl';

export default function CompanyInformation({ user, triggerNext, onGeoTagUpdate }) {
  const [formData, setFormData] = useState({
    companyName: '',
    companyWebsite: '',
    industry: '',
    houseNumber: '',
    streetAddress: '',
    city: '',
    county: '',
    postalCode: '',
    firstName: '',
    lastName: '',
    role: '',
    telephone: '',
    startDate: ''
  });
  const [roles, setRoles] = useState([]);
  const [geoTag, setGeoTag] = useState(null);
  const [geoTagLoading, setGeoTagLoading] = useState(false);
  const [geoTagSaving, setGeoTagSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [logo, setLogo] = useState(null);

  const inputClassName =
    'block w-full rounded-xl border border-indigo-200/70 bg-white/90 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200';

  const sectionCardClassName =
    'rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 p-6 shadow-[0_12px_30px_rgba(99,102,241,0.2)] backdrop-blur-xl';

  // ─── Fetch company profile ──────────────────────────────────────────────
  const fetchCompanyProfile = async () => {
    try {
      // Check cache first
      const cachedLogoData = localStorage.getItem('companyLogoData');
      const cachedTimestamp = localStorage.getItem('companyLogoTimestamp');
      if (cachedLogoData && cachedTimestamp) {
        const now = new Date().getTime();
        const oneDay = 24 * 60 * 60 * 1000;
        if (now - parseInt(cachedTimestamp) < oneDay) {
          setLogo(cachedLogoData);
        }
      }

      const response = await fetch('https://api.insightos.co.uk/api/company', {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) return;
      const payload = await response.json();
      const company = Array.isArray(payload?.data) ? payload.data[0] : payload?.data;
      if (!company) return;

      const addressParts = (company.Address || '').split(',').map((part) => part.trim());

      setFormData((prev) => ({
        ...prev,
        companyName: company.CompanyName || '',
        companyWebsite: company.Website || '',
        industry: company.Industry || '',
        houseNumber: company.CompanyNumber || '',
        streetAddress: addressParts[0] || '',
        city: company.City || addressParts[1] || '',
        county: addressParts[2] || '',
        postalCode: addressParts[3] || '',
      }));
      if (company.GeoTag) setGeoTag(company.GeoTag);

      // Set logo using helper
      const logoSrc = getCompanyLogoSrc(company);
      if (logoSrc) {
        setLogo(logoSrc);
        localStorage.setItem('companyLogoData', logoSrc);
        localStorage.setItem('companyLogoTimestamp', new Date().getTime().toString());
      }
    } catch (error) {
      console.error('Failed to load company profile:', error);
    }
  };

  // ─── Fetch user (staff) profile ──────────────────────────────────────
  const fetchUserProfile = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/staff', {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) return;
      const data = await response.json();
      const staffList = Array.isArray(data?.data) ? data.data : [];
      const currentUser = staffList.find((s) => s.Email === user?.email) || staffList[0];
      if (currentUser) {
        const nameParts = (currentUser.Name || '').split(' ');
        setFormData((prev) => ({
          ...prev,
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          role: currentUser.Role || '',
          telephone: currentUser.Telephone || '',
          startDate: currentUser.StartDate ? currentUser.StartDate.split('T')[0] : '',
        }));
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
    }
  };

  // ─── Fetch roles ──────────────────────────────────────────────────────
  const fetchRoles = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/allroles', {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) return;
      const data = await response.json();
      setRoles(data.data || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  // ─── Load all data ────────────────────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchCompanyProfile(), fetchUserProfile(), fetchRoles()]);
      setIsLoading(false);
    };
    loadData();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const companyData = {
      CompanyCode: user.companyCode,
      CompanyName: formData.companyName,
      Website: formData.companyWebsite,
      Industry: formData.industry,
      Address: `${formData.streetAddress}, ${formData.city}, ${formData.county}, ${formData.postalCode}`,
      City: formData.city,
      GeoTag: geoTag,
    };

    const userData = {
      Email: user.email,
      CompanyCode: user.companyCode,
      Name: `${formData.firstName} ${formData.lastName}`.trim(),
      Role: formData.role,
      Telephone: formData.telephone,
      StartDate: formData.startDate,
    };

    try {
      const response = await fetch('https://api.insightos.co.uk/api/companyonboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ companyData, userData }),
      });

      if (response.ok) {
        alert('Form submitted successfully');
        await Promise.all([fetchCompanyProfile(), fetchUserProfile()]);
        triggerNext();
      } else {
        alert('An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const persistGeoTag = async (value) => {
    if (!value) return;
    if (typeof onGeoTagUpdate === 'function') {
      await onGeoTagUpdate(value);
      return;
    }
    await fetch('https://api.insightos.co.uk/api/company-geotag', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ GeoTag: value }),
    });
  };

  const handleSaveGeoTag = async () => {
    if (!geoTag) {
      alert('Set your geotag location first.');
      return;
    }
    setGeoTagSaving(true);
    try {
      await persistGeoTag(geoTag);
      alert('Company geotag saved successfully.');
    } catch (error) {
      console.error('Failed to save company geotag:', error);
      alert('Failed to save company geotag. Please try again.');
    } finally {
      setGeoTagSaving(false);
    }
  };

  const geoLocate = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    setGeoTagLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const value = `${position.coords.latitude},${position.coords.longitude}`;
        setGeoTag(value);
        alert('GeoTag location captured. Click Save Geo Tag to store it.');
        setGeoTagLoading(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to retrieve your location. Please allow location access and try again.');
        setGeoTagLoading(false);
      }
    );
  };

  const uploadLogo = async (file) => {
    if (!user?.companyCode) {
      console.error('Company code not available');
      alert('Company code missing. Please refresh and try again.');
      return;
    }

    const payload = new FormData();
    payload.append('file', file);

    try {
      const response = await fetch(`https://api.insightos.co.uk/api/companypic/${user.companyCode}`, {
        method: 'POST',
        credentials: 'include',
        body: payload,
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Logo uploaded successfully', result);
        alert('Logo uploaded successfully.');
        // Refetch company profile to update the logo display
        await fetchCompanyProfile();
      } else {
        const errorText = await response.text();
        console.error('Upload failed:', errorText);
        alert('An error occurred while uploading the logo. Please try again.');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('An error occurred while uploading the logo. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
        </div>
      ) : (
        <>
          <div className="space-y-8">
            {/* ─── Company Profile ─────────────────────────────────────────── */}
            <div className={sectionCardClassName}>
              <h2 className="text-base font-semibold text-indigo-950">Company Profile</h2>
              <p className="mt-1 text-sm text-indigo-900/80">Review and update your company details.</p>

              <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-4">
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-900">
                    Company Name
                  </label>
                  <div className="mt-2">
                    <input
                      id="companyName"
                      name="companyName"
                      type="text"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="StaffShark"
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="sm:col-span-4">
                  <label htmlFor="companyWebsite" className="block text-sm font-medium text-gray-900">
                    Company Website
                  </label>
                  <div className="mt-2">
                    <input
                      id="companyWebsite"
                      name="companyWebsite"
                      type="text"
                      value={formData.companyWebsite}
                      onChange={handleChange}
                      placeholder="https://example.com"
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="col-span-full">
                  <label htmlFor="industry" className="block text-sm font-medium text-gray-900">
                    Industry
                  </label>
                  <div className="mt-2">
                    <textarea
                      id="industry"
                      name="industry"
                      rows={1}
                      value={formData.industry}
                      onChange={handleChange}
                      placeholder="Hospitality"
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="col-span-full">
                  <label htmlFor="houseNumber" className="block text-sm font-medium text-gray-900">
                    Companies House Number
                  </label>
                  <div className="mt-2">
                    <input
                      id="houseNumber"
                      name="houseNumber"
                      type="text"
                      value={formData.houseNumber}
                      onChange={handleChange}
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="col-span-full">
                  <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-900">
                    Street Address
                  </label>
                  <div className="mt-2">
                    <input
                      id="streetAddress"
                      name="streetAddress"
                      type="text"
                      value={formData.streetAddress}
                      onChange={handleChange}
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="city" className="block text-sm font-medium text-gray-900">
                    City
                  </label>
                  <div className="mt-2">
                    <input
                      id="city"
                      name="city"
                      type="text"
                      value={formData.city}
                      onChange={handleChange}
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="county" className="block text-sm font-medium text-gray-900">
                    County
                  </label>
                  <div className="mt-2">
                    <input
                      id="county"
                      name="county"
                      type="text"
                      value={formData.county}
                      onChange={handleChange}
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="postalCode" className="block text-sm font-medium text-gray-900">
                    Postal Code
                  </label>
                  <div className="mt-2">
                    <input
                      id="postalCode"
                      name="postalCode"
                      type="text"
                      value={formData.postalCode}
                      onChange={handleChange}
                      className={inputClassName}
                    />
                  </div>
                </div>

                {/* ─── Geotag, Logo & Branding ─── */}
                <div className="col-span-full mt-4">
                  <div className="rounded-2xl border border-indigo-200/70 bg-white/70 p-4 shadow-sm backdrop-blur-sm">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-sm font-medium text-gray-700">Location & Branding</span>
                      <div className="flex-1" />
                      <button
                        onClick={geoLocate}
                        disabled={geoTagLoading}
                        type="button"
                        className="relative z-10 rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 disabled:opacity-60"
                      >
                        {geoTagLoading ? 'Locating…' : '📍 Set Geo Tag'}
                      </button>
                      <button
                        onClick={handleSaveGeoTag}
                        disabled={!geoTag || geoTagSaving}
                        type="button"
                        className="relative z-10 rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 shadow-sm hover:bg-emerald-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-600 disabled:opacity-60"
                      >
                        {geoTagSaving ? 'Saving…' : '💾 Save Geo Tag'}
                      </button>
                      <label
                        className="relative z-10 cursor-pointer rounded-lg border border-purple-200 bg-white px-3 py-1.5 text-xs font-semibold text-purple-700 shadow-sm hover:bg-purple-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-600"
                      >
                        🖼️ Upload Logo
                        <input
                          type="file"
                          accept="image/*"
                          className="absolute inset-0 cursor-pointer opacity-0"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) uploadLogo(file);
                          }}
                        />
                      </label>
                      {logo && (
                        <img
                          src={logo}
                          alt="Company Logo"
                          className="h-10 w-10 rounded-full border border-white object-cover shadow"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                    </div>
                    {geoTag && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                        <span className="font-medium">GeoTag:</span>
                        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-700">{geoTag}</code>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ─── Your Info ──────────────────────────────────────────────── */}
            <div className={sectionCardClassName}>
              <h2 className="text-base font-semibold text-indigo-950">Your Info</h2>
              <p className="mt-1 text-sm text-indigo-900/80">Update your personal onboarding details.</p>

              <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-900">
                    First Name
                  </label>
                  <div className="mt-2">
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-900">
                    Last Name
                  </label>
                  <div className="mt-2">
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="role" className="block text-sm font-medium text-gray-900">
                    Role in Company
                  </label>
                  <div className="mt-2">
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className={inputClassName}
                    >
                      <option value="">Select your role</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.Name}>
                          {role.Name}
                        </option>
                      ))}
                    </select>
                    {roles.length === 0 && (
                      <p className="mt-1 text-xs text-amber-600">
                        No roles defined yet. Please complete the Role Calibration step first.
                      </p>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="telephone" className="block text-sm font-medium text-gray-900">
                    Personal Telephone Number
                  </label>
                  <div className="mt-2">
                    <input
                      id="telephone"
                      name="telephone"
                      type="tel"
                      value={formData.telephone}
                      onChange={handleChange}
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-900">
                    Start Date
                  </label>
                  <div className="mt-2">
                    <input
                      id="startDate"
                      name="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={handleChange}
                      className={inputClassName}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Buttons ──────────────────────────────────────────────────── */}
          <div className="mt-2 flex items-center justify-end gap-x-3">
            <button
              type="reset"
              className="rounded-xl border border-indigo-200 bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-900 shadow-sm hover:bg-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-component="company-onboarding-form"
              data-feature="submit_company_onboarding_post"
              className="rounded-xl border border-white/15 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-950/40 hover:from-indigo-800/95 hover:to-indigo-600/95"
            >
              Submit
            </button>
          </div>
        </>
      )}
    </form>
  );
}